import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router'

import { AppShell } from './components/AppShell'
import { ErrorBoundary } from './components/ErrorBoundary'
import { RouteScroll } from './components/RouteScroll'
import { AssistantProvider } from './contexts/AssistantContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { HomeSummaryProvider, useHomeSummary } from './contexts/HomeSummaryContext'
import { PendingCountProvider, usePendingCount } from './contexts/PendingCountContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { hasStateMap } from './lib/home'
import { useOnline } from './lib/useOnline'
import { AccountHome } from './pages/AccountHome'
import { DataRights } from './pages/DataRights'
import { AssistantPage } from './pages/assistant/AssistantPage'
import { AccountStatusScreen } from './pages/AccountStatus'
import { ASSISTANT_ENABLED, DATA_RIGHTS_ENABLED, EXPERIMENTS_ENABLED } from './lib/features'
import { NotFound } from './pages/NotFound'
import { Specimen } from './pages/Specimen'
import { Baseline } from './pages/product/Baseline'
import { History } from './pages/product/History'
import { Compare } from './pages/product/Compare'
import { Home } from './pages/product/Home'
import { Record } from './pages/product/Record'
import { RecordCapture } from './pages/product/RecordCapture'
import { ChooseExperiment } from './pages/product/ChooseExperiment'
import { RecordExperiment } from './pages/product/RecordExperiment'
import { ExperimentReport, ExperimentReportDraftBoundary } from './pages/product/ExperimentReport'
import { ExperimentResult } from './pages/product/ExperimentResult'
import { Experiments } from './pages/product/Experiments'
import { CheckIn } from './pages/product/CheckIn'
import { RecordingDetail } from './pages/product/RecordingDetail'
import { State } from './pages/product/State'
import { Today } from './pages/product/Today'
import { ForcedReset } from './pages/auth/ForcedReset'
import { ForgotPassword } from './pages/auth/ForgotPassword'
import { SignIn } from './pages/auth/SignIn'
import { SignUp } from './pages/auth/SignUp'
import { VerifyEmail } from './pages/auth/VerifyEmail'
import { ApprovalQueue } from './pages/admin/ApprovalQueue'
import { Audit } from './pages/admin/Audit'
import { UserDetail } from './pages/admin/UserDetail'
import { Users } from './pages/admin/Users'
import { Settings } from './pages/admin/Settings'

export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        {/* AuthProvider sits OUTSIDE BrowserRouter, matching AIVY. It therefore
            has no router context and must never navigate -- it resolves state
            and the guards below route on it. A provider that navigated would
            race the guards and bounce a freshly-signed-in user back to /sign-in. */}
        {/* OUTSIDE BrowserRouter, and that placement is the point.
            §7.1 asks for an offline state on EVERY screen, and `ProblemNotice`
            can only speak for a screen that made a request and watched it fail:
            `/account` fetches nothing at all and the five auth screens render
            Amplify's errors through a plain `Notice`, so on six screens the
            word "offline" was unreachable in any state. Mounted once here it
            covers those and the not-found route, and a screen written in 1.10
            gets it without being told -- the structural argument
            `disclaimerPairing.test.tsx` already makes for the disclaimer.

            NOT the error-boundary fallback, which an earlier version of this
            comment claimed. This sits INSIDE `ErrorBoundary`, and that boundary
            swaps its children for a standalone fallback, so a crashed app shows
            the fallback and not this. Moving it outside would put it where a
            crash cannot remove it; it is left here because a banner above a
            crash screen is noise, and that is a choice rather than an oversight.

            It needs no router context, which is why it can sit this high. */}
        <OfflineBanner />
        <AuthProvider>
          <BrowserRouter>
            {EXPERIMENTS_ENABLED && <ExperimentReportDraftBoundary />}
            <RouteScroll />
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/sign-in" element={<PublicOnly><SignIn /></PublicOnly>} />
      <Route path="/sign-up" element={<PublicOnly><SignUp /></PublicOnly>} />
      <Route path="/verify-email" element={<PublicOnly><VerifyEmail /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      {/* Reachable directly as well as inline from sign-in, so a challenge that
          survives a reload is not a dead end. */}
      <Route path="/set-password" element={<ForcedReset onDone={() => undefined} />} />

      <Route
        path="/*"
        element={
          <RequireAuth>
            <ConsoleRoutes />
          </RequireAuth>
        }
      />
    </Routes>
  )
}

function ConsoleRoutes() {
  const { account } = useAuth()
  return (
    <PendingCountProvider>
      {/* Sprint 1.9.9 P6: the shell reads `/home` once, here, under the gate,
          so a gated account never makes the request and Home reuses the read. */}
      <HomeSummaryProvider>
        {/* agent-1-web: the assistant's one conversation, app-wide. Only in a
            build with the assistant, and only past the approval gate, so a
            gated account never reaches a chat route. */}
        {ASSISTANT_ENABLED ? (
          <AssistantProvider accountId={account?.user_id ?? null} experimentsEnabled={EXPERIMENTS_ENABLED && account != null && 'experiments_enabled_for_me' in account && account.experiments_enabled_for_me === true}>
            <Console />
          </AssistantProvider>
        ) : (
          <Console />
        )}
      </HomeSummaryProvider>
    </PendingCountProvider>
  )
}

function Console() {
  const { account, signOut } = useAuth()
  const { count } = usePendingCount()
  // Sprint 1.9.9 P6: State is in the nav exactly when the page would render —
  // one test, `hasStateMap`, over the shell's one `/home` read.
  const { state: home } = useHomeSummary()
  const isAdmin = account?.role === 'admin'
  return (
    <AppShell
      account={account ? { email: account.email, role: account.role, experiments_enabled_for_me: account.experiments_enabled_for_me } : undefined}
      counts={{ pendingCount: isAdmin ? count ?? undefined : undefined }}
      showAdminNav={isAdmin}
      showStateNav={hasStateMap(home)}
      onSignOut={() => void signOut()}
    >
      <Routes>
        {/* A non-admin's home is Home (`/home`), from Sprint 1.9.1 -- the
            overview that is never blank, where Report is the readout of one
            recording and is blank until one exists. It was Today in 1.9 and the
            account screen in 1.8. An operator's is still the approval queue:
            the product screens are theirs to use as a user, but the queue is
            the thing they signed in to do. */}
        <Route path="/" element={<Navigate to={isAdmin ? '/admin/queue' : '/home'} replace />} />
        <Route path="/home" element={<Home />} />
        {/* Sprint 1.9.9 (R40): Today is Report. The page keeps its component
            name, and the old address is a REPLACING redirect, so a bookmark or a
            link still in someone's history lands on Report without leaving
            `/today` behind it on the back button. */}
        <Route path="/report" element={<Today />} />
        <Route path="/today" element={<Navigate to="/report" replace />} />
        {/* Sprint 1.9.9 S6: declared for everyone, and the page decides — a
            loading card while `/home` loads, NotFound when the reading has no
            state map. The nav offers it only when the page would render. */}
        <Route path="/state" element={<State />} />
        <Route path="/record" element={<Record />} />
        {/* Sprint 1.9.7: the product records. Canvas 05 was drawn for this
            address; the page connects the headband over Web Bluetooth, fits it,
            records the three quiet minutes and hands the session to processing. */}
        <Route path="/record/connect" element={<RecordCapture />} />
        {EXPERIMENTS_ENABLED && account?.experiments_enabled_for_me === true && <>
          <Route path="/record/experiment" element={<ChooseExperiment />} />
          <Route path="/record/experiment/:runId" element={<RecordExperiment />} />
          <Route path="/record/experiment/:runId/report" element={<ExperimentReport />} />
          <Route path="/experiment-runs/:id" element={<ExperimentResult />} />
          <Route path="/experiments" element={<Experiments />} />
        </>}
        {/* Beacon, Canvas 04 §01: the optional three-minute check-in after a recording. */}
        <Route path="/check-in" element={<CheckIn />} />
        <Route path="/baseline" element={<Baseline />} />
        <Route path="/history" element={<History />} />
        {EXPERIMENTS_ENABLED && account?.experiments_enabled_for_me === true && <Route path="/history/compare/:a/:b" element={<Compare />} />}
        {/* The detail view is a CHILD of the list, so "← History" is a real
            parent rather than a guess. `recordingId` is user-supplied and
            deliberately ungated here: ownership is not knowable client-side,
            and a client that guessed would produce a screen distinguishing
            "yours but absent" from "someone else's" — which the backend
            spent code to make indistinguishable. */}
        <Route path="/history/:recordingId" element={<RecordingDetail />} />
        <Route path="/account" element={<AccountHome />} />
        {/* agent-1-web W5: the assistant's own page, behind the module-scope
            flag like the data-rights route below, and in the same commit as its
            nav entry (`lib/nav.ts`). */}
        {ASSISTANT_ENABLED && <Route path="/assistant" element={<AssistantPage />} />}
        {/* §6.2 ships export and deletion DARK. A build-time flag, read at module
            scope, so Vite eliminates the branch and the screens are not merely
            unrouted but absent from the bundle -- the same shape as the token
            gallery below, and measured the same way. A route rather than a
            section on /account, because §6.2 asks for direct-route behaviour to
            be tested and only a route can be navigated to directly: with the
            flag off this falls to `*` and says the page does not exist, which
            for a user of the shipped product is true. */}
        {DATA_RIGHTS_ENABLED && <Route path="/account/data" element={<DataRights />} />}
        {/* §5.8 role gating. The admin routes are not RENDERED for a non-admin,
            so no admin API call can be issued on their behalf — the same
            argument as the approval gate. `require_admin` on the server is the
            enforcement; this is hygiene, and it is what stops the console from
            firing a burst of 403s at someone who simply is not an operator. */}
        {isAdmin && <Route path="/admin/queue" element={<ApprovalQueue />} />}
        {isAdmin && <Route path="/admin/users" element={<Users />} />}
        {isAdmin && <Route path="/admin/users/:userId" element={<UserDetail />} />}
        {isAdmin && <Route path="/admin/settings" element={<Settings />} />}
        {isAdmin && <Route path="/admin/audit" element={<Audit />} />}
        {/* A non-admin who follows an admin link -- a bookmark, a pasted URL --
            is sent to their own home rather than shown "page not found", which
            would be true but unhelpful and slightly misleading: the page exists,
            it is simply not theirs. One home, not two: this and `/` agree, so
            where a non-admin lands does not depend on how they arrived. Both
            name `/home` from Sprint 1.9.1, and `gatedAccount.test.tsx` pins each
            separately. */}
        {!isAdmin && <Route path="/admin/*" element={<Navigate to="/home" replace />} />}
        {import.meta.env.VITE_ENABLE_SPECIMEN === 'true' && (
          <Route path="/_specimen" element={<Specimen />} />
        )}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  )
}

/**
 * The gate. Routes off RESOLVED state, never off the outcome of a call — which
 * is AIVY's "bug B" fix, and the reason sign-in does not navigate on success.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { account, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) return <AuthLoading />
  if (!account) {
    // Remember where they were going, so approval or sign-in returns them to it
    // rather than to the console root.
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />
  }

  // The approval gate (§3.5). A non-active account gets its own screen and the
  // app makes NO product API calls on its behalf.
  //
  // Enforced by NOT RENDERING the console rather than by hiding it: every call
  // this app makes is issued from a mounted component, so a gate that returns
  // before `children` cannot leak one. A gate that rendered the console behind
  // an overlay would still fire every load effect inside it -- each one a 403
  // the user never sees, and a burst of them the moment they sign in.
  if (account.status !== 'active') {
    return <AccountStatusScreen status={account.status} />
  }

  return <>{children}</>
}

/** Signed-in users have no business on the sign-in form. */
function PublicOnly({ children }: { children: React.ReactNode }) {
  const { account, isLoading } = useAuth()
  if (isLoading) return <AuthLoading />
  if (account) return <Navigate to="/" replace />
  return <>{children}</>
}

/**
 * IN NORMAL FLOW AND FIRST IN THE DOCUMENT, not fixed.
 *
 * A fixed bar at the top covers the `.shell__topbar` -- which is sticky below
 * 1024px (from 1024px `AppShell` does not render the top bar) -- and the drawer
 * trigger inside it; a fixed bar at the bottom collides with
 * `.row-menu--sheet`, which is `bottom: 0`. In flow it is zero-height whenever
 * there is nothing to say, so it costs nothing on every screen that is fine.
 *
 * `role="status"` and not `role="alert"`: losing a connection is not an error
 * in the thing the user was doing, and interrupting them mid-sentence to say so
 * is the behaviour `Notice`'s own header argues against. Always mounted and
 * empty when online, which is the shape trap 28 asks for.
 */
function OfflineBanner() {
  const online = useOnline()
  return (
    <div className="offline-banner" role="status">
      {online ? '' : 'You are offline. Nothing you do here will reach the server until you reconnect.'}
    </div>
  )
}

function AuthLoading() {
  return (
    <div className="centred-page">
      <div className="auth-loading" role="status">
        <span className="spinner" aria-hidden="true" />
        <span>Checking your session…</span>
      </div>
    </div>
  )
}
